import {
  MouseEvent,
  ReactElement,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EContainerIconSize, EIconSize } from 'utils/constants/app/ui';
import {
  HANDICAPS,
  OVER_UNDERS,
  WINNER_MARKETS
} from 'utils/constants/sportsbook/grouped-market-names';
import { NotRealSportsForDifFormats } from 'utils/constants/sportsbook/sport-list';
import {
  TWO_WAY_OUTCOME_SPORTS,
  USA_SPEC_SPORTS
} from 'utils/constants/sportsbook/usa-specific-sports';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { ADD_GAME_CARDS } from 'utils/constants/tour';
import { isMobile } from 'utils/is-mobile';
import { checkIsNotESportType } from 'utils/sportsbook/check-type-is-not-sportbook';
import {
  checkIsMarketInGroupWithOvertime,
  findWinnerMarkersForUsaSports,
  getOrderedMarketsForUsaSports
} from 'utils/sportsbook/get-usa-ordered-markets';
import { handleRedirectToSportsBook } from 'utils/sportsbook/handleRedirectToSportsBook';
import { ETeamNumber } from 'interfaces/sportsbook';
import { ESportsbookLayoutTypes } from 'interfaces/sportsbook-configs';
import { Event, Game, Market } from 'interfaces/sportsbook-data-levels';
import { AFTER_SIGN_IN_ACTION_TYPES } from 'services/afterSignIn';
import { DetailedTeamsInfo } from './__partials/DetailedTeamsInfo';
import { Odd } from './__partials/Odd';
import { getEmptyMarketsForNotClassicView } from './__partials/utils/getEmptyMarketsForNotClassicView';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { FavoriteButtonGame } from 'components/FavoriteButton';
import { GlobalIcon } from 'components/GlobalIcon';
import { TeamLogo } from 'components/TeamLogo';
import { GameTime } from 'newcomponents/Shared/GameTime';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { useOpenBetBuilder } from 'newhooks/sportsbook/useOpenBetBuilder';
import { getSportIdByAlias } from 'store/selectors/new-sportsbook';
import './index.less';

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

type TMatchCardInlineUSAProps = {
  game: Game & { regionAlias?: string };
  competitionName: string;
  sportAlias?: string;
  eventType?: string;
  comp?: {
    id?: number;
  };
  showGameTime?: boolean;
  includeRedirect?: boolean;
  externalRedirect?: (
    e: MouseEvent | SyntheticEvent,
    gameId: number,
    gameType: number
  ) => void;
  eventsElement?: boolean;
  withMoreWagesWrapper?: boolean;
  withTeamLogo?: boolean;
  showMarketName?: boolean;
  matchStartTimeInFooter?: boolean;
  isLive?: boolean;
  detailedTeamsInfo?: boolean;
  setMarketNames?: (arg: string[]) => void;
  showFavoriteButton?: boolean;
  showBBIcon?: boolean;
  regionAlias?: string;
  isLast?: boolean;
};

const matchCardInlineUSADefaultProps = {
  game: {},
  competitionName: '',
  showGameTime: true,
  includeRedirect: true
} as TMatchCardInlineUSAProps;

export const MatchCardInlineUSA = ({
  game,
  competitionName,
  sportAlias,
  eventType,
  comp,
  regionAlias,
  showGameTime,
  includeRedirect,
  externalRedirect,
  withMoreWagesWrapper,
  showMarketName = true,
  matchStartTimeInFooter,
  isLive,
  detailedTeamsInfo,
  setMarketNames,
  showFavoriteButton,
  showBBIcon,
  isLast
}: TMatchCardInlineUSAProps = matchCardInlineUSADefaultProps): ReactElement => {
  const { t } = useTranslation();
  const configs = useSportsbookConfigs();
  const { params } = useSbRouteMatch();

  const [isClassic, setIsClassic] = useState(
    USA_SPEC_SPORTS.includes(sportAlias || '')
  );

  const [isClassicColumn, setIsClassicColumn] = useState(
    TWO_WAY_OUTCOME_SPORTS.includes(sportAlias || '')
  );

  const currentSportId = useSelector(
    getSportIdByAlias(sportAlias as string, configs.type)
  );

  const betBuilderProps = {
    sportId: currentSportId,
    fixtureId: game.sportcast_id,
    gameTitle: `${game.team1_name} - ${game.team2_name}`,
    afterSignInActonName: `MatchCardInlineUSA_${game.id}_${AFTER_SIGN_IN_ACTION_TYPES.TOGGLE_BET_BUILDER_MODAL}`
  };

  const { openBetBuilder } = useOpenBetBuilder(betBuilderProps);

  const isLiveMatch = isLive || eventType === 'liveEvents' || game.type === 1;

  const handleRedirect = useCallback(
    (
      region: string,
      competitionId: number | undefined,
      gameId: number,
      e: SyntheticEvent
    ) => {
      externalRedirect
        ? externalRedirect(e, gameId, game.type)
        : handleRedirectToSportsBook(
            eventType as string,
            region,
            competitionId,
            gameId,
            sportAlias
          );
    },
    [sportAlias, externalRedirect, game.type]
  );

  const isOutRight = game.show_type === 'OUTRIGHT';

  const winnerMarket = useMemo(() => {
    const marketsArr = Object.values(game.market || {}).filter(el => !!el);

    const winner =
      marketsArr.find(market => WINNER_MARKETS.includes(market.type)) ||
      getEmptyMarketsForNotClassicView(
        TWO_WAY_OUTCOME_SPORTS.includes(sportAlias || '')
      );

    return winner as Market;
  }, [game.market, sportAlias]);

  const markets = useMemo<Market[]>(() => {
    const markets = Object.values(game.market || {});
    const orderedArray = [];
    const handicaps = getOrderedMarketsForUsaSports(markets, HANDICAPS);

    orderedArray.push(
      handicaps[0] || {
        id: -1,
        type: 'Handicap',
        event: {
          '-1': {
            id: -1
          },
          '-2': {
            id: -2
          }
        }
      }
    );

    const overUnders = getOrderedMarketsForUsaSports(markets, OVER_UNDERS);

    orderedArray.push(
      overUnders[0] || {
        id: -1,
        type: 'OverUnder',
        event: {
          '-1': {
            id: -1
          },
          '-2': {
            id: -2
          }
        }
      }
    );

    const winner = findWinnerMarkersForUsaSports(markets, [
      'P1P2',
      'W1W2',
      'MatchWinner'
    ]);

    orderedArray.push(
      winner || {
        id: -1,
        type: 'MatchWinner',
        event: {
          '-1': {
            id: -1
          },
          '-2': {
            id: -2
          }
        }
      }
    );

    return orderedArray as Market[];
  }, [game]);

  useEffect(() => {
    setIsClassic(USA_SPEC_SPORTS.includes(sportAlias || ''));
    setIsClassicColumn(TWO_WAY_OUTCOME_SPORTS.includes(sportAlias || ''));
  }, [sportAlias]);

  const getSportsbookMarket = (marketItem: Market | Event) => {
    if (checkIsMarketInGroupWithOvertime(HANDICAPS, marketItem?.type)) {
      if (sportAlias === 'Baseball') {
        return 'runLine';
      } else {
        return 'marketSpread';
      }
    } else if (
      checkIsMarketInGroupWithOvertime(OVER_UNDERS, marketItem?.type)
    ) {
      return 'marketTotal';
    }

    return 'marketMoney';
  };

  const marketNames = useMemo(() => {
    if (game.show_type === 'OUTRIGHT') {
      return [];
    }

    const marketTypes = isClassic
      ? markets
      : sortByOrder(Object.values(winnerMarket?.event || {}));

    const _marketNames = marketTypes.map((marketItem, index) => {
      if (isClassic) {
        return t(`sportsbook.${getSportsbookMarket(marketItem)}`);
      } else {
        return !isClassicColumn
          ? index === 0
            ? '1'
            : marketTypes.length > 2
            ? index === 2
              ? '2'
              : 'X'
            : '2'
          : marketItem.name;
      }
    });

    return _marketNames;
  }, [isClassic, markets, winnerMarket, isClassicColumn, game.show_type]);

  useEffect(() => {
    if (marketNames) {
      setMarketNames?.(marketNames);
    }
  }, [marketNames]);

  const isSportsbook =
    configs.spbLayoutType === ESportsbookLayoutTypes.AMERICAN;

  const longFormatChecker = NotRealSportsForDifFormats.includes(
    params.category
  );

  return (
    <ConditionalWrapper
      condition={!!withMoreWagesWrapper}
      wrapper={children => (
        <div
          className={cc([
            'matchCardInline__moreWagesWrapper',
            { 'matchCardInline__moreWagesWrapper--lastItem': isLast }
          ])}
          onClick={e =>
            handleRedirect(
              game.region_alias || regionAlias || '',
              comp?.id,
              game.id,
              e
            )
          }
        >
          {children}
          <div
            className={cc([
              'matchCardInline__moreWages',
              {
                'matchCardInline__moreWages--mobile': isMobile(),
                'matchCardInline__moreWages--lastItem': isLast
              }
            ])}
          >
            <span>
              {matchStartTimeInFooter && (
                <GameTime
                  isLive={isLiveMatch}
                  game={game}
                  currentSport={sportAlias}
                  dir="row"
                  reverse={!isLiveMatch}
                  format={SpringConfigs.DT.longDate}
                  timeOnly
                />
              )}
              <span className="matchCardInline__moreInfo">
                {game.text_info || ''}
              </span>
            </span>
            <span className="matchCardInline__moreWages__title">
              {t('sportsbook.moreWages')}{' '}
              {!!game.markets_count && `[+${game.markets_count}]`}
            </span>
          </div>
        </div>
      )}
    >
      <div
        {...(SpringConfigs.USER_PERSONALIZATION_ENABLED
          ? { 'data-tour': ADD_GAME_CARDS }
          : {})}
        className={cc([
          'matchCardInline__wrapper--usa',
          {
            'matchCardInline__wrapper--usa--noBorder': !includeRedirect,
            'matchCardInline__wrapper--usa--withBorderRadius': isLiveMatch
          }
        ])}
        {...(includeRedirect && {
          onClick: e =>
            handleRedirect(
              game.region_alias || regionAlias || '',
              comp?.id,
              game.id,
              e
            )
        })}
      >
        <div
          className={cc([
            'matchCardInline__info',
            {
              'matchCardInline__info--fixedWidth': isSportsbook && isMobile(),
              'matchCardInline__info--emptyOdds':
                isOutRight && withMoreWagesWrapper,
              'matchCardInline__info--outright': isOutRight
            }
          ])}
        >
          {showGameTime && (
            <div className="matchCardInline__leftGameInfo">
              {showFavoriteButton && isMobile() && (
                <div className="favoriteIconWrapperMobile">
                  <FavoriteButtonGame
                    size={EIconSize._16}
                    game={{ id: game.id, type: game.type }}
                    iconContainerSize={EContainerIconSize.MIDDLE}
                    iconMargin={null}
                  />
                </div>
              )}
              <GameTime
                isLive={isLiveMatch}
                game={game}
                currentSport={sportAlias}
                dir="row"
                timeOnly={!!externalRedirect && !longFormatChecker}
                format={SpringConfigs.DT.longDate}
              />
            </div>
          )}
          {detailedTeamsInfo ? (
            <DetailedTeamsInfo
              game={game}
              showScores={isLiveMatch}
              sportAlias={sportAlias || ''}
            />
          ) : (
            <div className="matchCardInline__teamNames-container">
              <div
                className={cc([
                  'matchCardInline__teamNames',
                  {
                    'matchCardInline__teamNames--margin':
                      !isClassicColumn && showMarketName,
                    'matchCardInline__teamNames--outright': isOutRight
                  }
                ])}
              >
                <div>
                  <div
                    className={cc([
                      'matchCardInline__teamInfoWrapper',
                      {
                        globalEllipsis: !isOutRight
                      }
                    ])}
                  >
                    {isSportsbook ? (
                      <TeamLogo
                        isHomeOrAway={ETeamNumber.HOME}
                        isNotESport={checkIsNotESportType(game)}
                        teamId={game.team1_id}
                        size="sm"
                        sizeByPixels={20}
                      >
                        <span className="iconWithTeamName">
                          {String(game.team1_name)
                            .substring(0, 2)
                            .toUpperCase()}
                        </span>
                      </TeamLogo>
                    ) : null}
                    <span
                      className={cc([
                        {
                          matchCardInline__teamName: isSportsbook,
                          globalEllipsis: !isOutRight
                        }
                      ])}
                    >
                      {game.team1_name}
                    </span>
                  </div>
                  <div className="matchCardInline__teamNames__score">
                    {game.info?.score1 && !isOutRight ? game.info.score1 : ''}
                  </div>
                </div>
                {!!game.team2_name && (
                  <div>
                    <div className="matchCardInline__teamInfoWrapper globalEllipsis">
                      {isSportsbook ? (
                        <TeamLogo
                          isHomeOrAway={ETeamNumber.AWAY}
                          isNotESport={checkIsNotESportType(game)}
                          teamId={game.team2_id}
                          size="sm"
                          sizeByPixels={20}
                        >
                          <span className="iconWithTeamName">
                            {String(game.team2_name)
                              .substring(0, 2)
                              .toUpperCase()}
                          </span>
                        </TeamLogo>
                      ) : null}
                      <span
                        className={
                          isSportsbook
                            ? 'matchCardInline__teamName globalEllipsis'
                            : ''
                        }
                      >
                        {game.team2_name}
                      </span>
                    </div>
                    <div className="matchCardInline__teamNames__score">
                      {game.info?.score2 && !isOutRight ? game.info.score2 : ''}
                    </div>
                  </div>
                )}
              </div>
              {showBBIcon &&
                game.sportcast_id &&
                SpringConfigs.BET_BUILDER_SWITCHER && (
                  <span className="matchCardInline__BB-icon">
                    <GlobalIcon
                      lib={
                        SpringConfigs.IS_AMERICAN_LAYOUT ? 'generic' : 'sports'
                      }
                      name={
                        SpringConfigs.IS_AMERICAN_LAYOUT
                          ? 'SGPIcon'
                          : 'betBuilder'
                      }
                      theme="default"
                      size={EIconSize._16}
                      onClick={(e: SyntheticEvent) => openBetBuilder(e)}
                    />
                  </span>
                )}
            </div>
          )}
        </div>
        <div
          className={cc([
            'matchCardInline__odds',
            {
              'matchCardInline__odds--column':
                isClassicColumn && !detailedTeamsInfo,
              'matchCardInline__odds--emptyOdds':
                isOutRight && withMoreWagesWrapper,
              'matchCardInline__odds--outright': isOutRight
            }
          ])}
        >
          <div
            className={cc([
              'matchCardInline__oddsWrapper',
              {
                'USA__outright-column': isOutRight
              }
            ])}
          >
            {isOutRight
              ? null
              : isClassic
              ? markets.map((marketItem, index) => (
                  <div
                    {...(isMobile() && { dataTestId: 'odd' })}
                    key={index}
                    className={cc([
                      'matchCardInline__oddGroup',
                      {
                        'matchCardInline__oddGroup--mobile': isMobile()
                      }
                    ])}
                  >
                    {showMarketName && (
                      <div className="matchCardInline__marketName">
                        {t(`sportsbook.${getSportsbookMarket(marketItem)}`)}
                      </div>
                    )}
                    {sortByOrder(Object.values(marketItem?.event || {})).map(
                      (eventItem, index) => (
                        <Odd
                          key={index}
                          game={game}
                          event={eventItem}
                          market={marketItem}
                          sportAlias={sportAlias}
                          sportId={currentSportId}
                          competitionName={competitionName}
                          isLive={isLiveMatch}
                          isUsa
                          {...(isMobile() && { dataTestId: 'odd' })}
                        />
                      )
                    )}
                  </div>
                ))
              : sortByOrder(Object.values(winnerMarket?.event || {})).map(
                  (event, index, array) => (
                    <div
                      key={index}
                      className={cc([
                        'matchCardInline__oddGroup',
                        {
                          'matchCardInline__oddGroup--mobile': isMobile(),
                          'matchCardInline__oddGroup--fullHeight':
                            isMobile() && isSportsbook
                        }
                      ])}
                    >
                      {!isClassicColumn && showMarketName ? (
                        <div className="matchCardInline__marketName">
                          {index === 0
                            ? '1'
                            : array.length > 2
                            ? index === 2
                              ? '2'
                              : 'X'
                            : '2'}
                        </div>
                      ) : isMobile() && detailedTeamsInfo ? (
                        <div className="matchCardInline__marketName" />
                      ) : null}
                      <Odd
                        game={game}
                        event={event}
                        market={winnerMarket}
                        sportAlias={sportAlias}
                        sportId={currentSportId}
                        competitionName={competitionName}
                        isLive={isLiveMatch}
                        isUsa
                        dataTestId="odd"
                      />
                    </div>
                  )
                )}
          </div>

          {showFavoriteButton && !isMobile() && (
            <span className="favoriteIconWrapper">
              <FavoriteButtonGame
                game={{ id: game.id, type: game.type }}
                size={EIconSize._16}
                iconContainerSize={EContainerIconSize.SMALL}
              />
            </span>
          )}
        </div>
      </div>
    </ConditionalWrapper>
  );
};
